import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useSelector } from 'react-redux';
import { RootState } from '../../store';
import { getTimezones, getUnitsOfMeasurements, getUserPreferences, saveUserPreferences } from '../../Services/serviceURL';
import { getUserAccessById, decodeToken } from '../../Services/Users.service';
import Loader from '../common/loader/loader';
import './UserPreferences.css';

interface UserPreferencesProps {
    onClose: () => void;
}

interface UserSettings {
    timeZone: string;
    uom: string;
}

interface TimezoneOption {
    id: string;
    text: string;
    value: string;
}

interface UserData {
    firstName?: string;
    lastName?: string;
    email?: string;
    role?: string;
    userType?: string;
    permissions?: string[];
    // Add other possible fields from getUserAccessById response
    userId?: string;
    customerId?: string;
    userName?: string;
}

interface TokenUserInfo {
    email?: string;
    name?: string;
    role?: string;
    userType?: string;
    userId?: string;
}

const getUsernameFromEmail = (email: string | null): string => {
    if (!email) return 'User';
    const username = email.split('@')[0];
    return username
        .replaceAll('.', ' ')
        .replaceAll('_', ' ')
        .split(' ')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ');
};

const getDisplayUsername = (
    userData: UserData | null,
    tokenUserInfo: TokenUserInfo | null,
    userId: string | null | undefined,
    tokenUserId: string | null,
    devUserId: string | null
): string => {
    if (userData?.firstName && userData?.lastName) {
        return `${userData.firstName} ${userData.lastName}`;
    }

    if (tokenUserInfo?.name) {
        return tokenUserInfo.name;
    }

    const emailToUse = userId ||
        tokenUserId ||
        tokenUserInfo?.email ||
        devUserId ||
        localStorage.getItem('loginemail') ||
        localStorage.getItem('userId');

    return getUsernameFromEmail(emailToUse);
};

const UserPreferences: React.FC<UserPreferencesProps> = ({ onClose }) => {
    const userId = useSelector((state: RootState) => state.auth.userId); // This is the email
    const userRole = useSelector((state: RootState) => state.auth.userRole);

    // Try to decode token if available (for localhost development)
    const tryGetUserFromToken = () => {
        const accessToken = localStorage.getItem('access_token');
        if (!accessToken) {
            return null;
        }

        const decoded = decodeToken(accessToken);
        if (!decoded) {
            return null;
        }

        // Check various possible properties in the token
        const decodedAny = decoded as any; // Cast to any to access all possible properties
        const possibleUserIds = [
            decoded?.unique_name,
            decodedAny?.['http://schemas.xmlsoap.org/ws/2005/05/identity/claims/emailaddress'], // Email from claims
            decodedAny?.email,
            decodedAny?.sub,
            decodedAny?.preferred_username,
            decodedAny?.upn
        ];

        const foundUserId = possibleUserIds.find(id => id && id.trim() !== '');
        return foundUserId || null;
    };

    const tokenUserId = tryGetUserFromToken();

    // Extract full user info from token
    const extractUserInfoFromToken = () => {
        const accessToken = localStorage.getItem('access_token');
        if (!accessToken) {
            return null;
        }

        const decoded = decodeToken(accessToken) as any;
        if (!decoded) {
            return null;
        }

        return {
            email: decoded?.['http://schemas.xmlsoap.org/ws/2005/05/identity/claims/emailaddress'],
            name: decoded?.['http://schemas.xmlsoap.org/ws/2005/05/identity/claims/name'],
            role: decoded?.['http://schemas.microsoft.com/ws/2008/06/identity/claims/role'],
            userType: decoded?.UserType,
            userId: decoded?.UserId
        };
    };

    const tokenUserInfo = extractUserInfoFromToken();

    // For localhost development, add a fallback
    const devUserId = globalThis.location?.hostname === 'localhost' && !userId && !tokenUserId
        ? 'developer@localhost.com'
        : null;

    const [userData, setUserData] = useState<UserData | null>(null);
    const [settings, setSettings] = useState<UserSettings>({
        timeZone: '',
        uom: 'Metric'
    });
    const [isLoadingSettings, setIsLoadingSettings] = useState(true);
    const [timeZoneOptions, setTimeZoneOptions] = useState<{ value: string; label: string; }[]>([]);
    const [isLoadingTimeZones, setIsLoadingTimeZones] = useState(true);
    const [timeZoneSearch, setTimeZoneSearch] = useState('');
    const [isTimeZoneDropdownOpen, setIsTimeZoneDropdownOpen] = useState(false);
    const [filteredTimeZones, setFilteredTimeZones] = useState<{ value: string; label: string; }[]>([]);
    const [selectedTimeZoneLabel, setSelectedTimeZoneLabel] = useState<string>('');
    const [isLoadingUnits, setIsLoadingUnits] = useState(true);
    const [isSaving, setIsSaving] = useState(false);
    const [saveError, setSaveError] = useState<string | null>(null);
    const [saveSuccess, setSaveSuccess] = useState(false);
    const dialogRef = useRef<HTMLDialogElement>(null);

    const displayUsername = getDisplayUsername(userData, tokenUserInfo, userId, tokenUserId, devUserId);

    const handleSave = async () => {
        // For saveUserPreferences API, use UserId from token (MongoDB ObjectId format)
        const userIdForSave = tokenUserInfo?.userId || userId || tokenUserId || devUserId;

        if (!userIdForSave) {
            setSaveError('User ID not found. Please log in again.');
            return;
        }

        try {
            setIsSaving(true);
            setSaveError(null);
            setSaveSuccess(false);

            // Store current preferences to compare with new ones
            const currentPreferences = await getUserPreferences(userIdForSave).catch(() => ({ timeZone: '', uom: 'Metric' }));
            const timeZoneChanged = (currentPreferences?.timeZone || '') !== settings.timeZone;

            // Save the preferences using the UserId (MongoDB ObjectId)
            await saveUserPreferences(userIdForSave, {
                timeZone: settings.timeZone,
                uom: settings.uom
            });
             localStorage.setItem('userTimezone', settings.timeZone);
              const shortName = getTimezoneShortName(settings.timeZone);
        localStorage.setItem('userTimezoneShort', shortName);
            setSaveSuccess(true);

            // If timezone changed, refresh the application after showing success message
            if (timeZoneChanged) {
                setTimeout(() => {
                    globalThis.location.reload();
                }, 1500);
            } else {
                // Auto-close the modal after 1.5 seconds on successful save (only if timezone didn't change)
                setTimeout(() => {
                    onClose();
                }, 1500);
            }

        } catch (error) {
            console.error(' Error saving preferences:', error);
            setSaveError('Failed to save preferences. Please try again.');
        } finally {
            setIsSaving(false);
        }
    };
   
// Helper function to get timezone short name
const getTimezoneShortName = (ianaTimezone: string): string => {
    try {
        const now = new Date();
        const formatter = new Intl.DateTimeFormat('en-US', {
            timeZone: ianaTimezone,
            timeZoneName: 'short'
        });
        
        const parts = formatter.formatToParts(now);
        const timeZonePart = parts.find(part => part.type === 'timeZoneName');
        
        return timeZonePart?.value || 'UTC';
    } catch (error) {
        console.error('Error getting timezone short name:', error);
        return 'UTC';
    }
};
    useEffect(() => {
        const loadTimeZones = async () => {
            try {
                setIsLoadingTimeZones(true);
                const timezones = await getTimezones();

                if (timezones.length === 0) {
                    setTimeZoneOptions([]);
                    return;
                }

                // Map API response to dropdown options
                const formattedOptions = timezones.map((timezone: TimezoneOption) => ({
                    value: timezone.value,
                    label: timezone.text
                }));

                setTimeZoneOptions(formattedOptions);
            } catch (error) {
                console.error(' Error loading time zones:', error);
                // Set empty array if API fails - no fallback options
                setTimeZoneOptions([]);
            } finally {
                setIsLoadingTimeZones(false);
            }
        };

        const loadUnitsOfMeasurements = async () => {
            try {
                setIsLoadingUnits(true);
                await getUnitsOfMeasurements();
            } catch (error) {
                console.error('Error loading units of measurements:', error);
            } finally {
                setIsLoadingUnits(false);
            }
        };

        const loadUserData = async () => {
            // Try to get userId from multiple sources including token and dev fallback
            const currentUserId = userId ||
                tokenUserId ||
                tokenUserInfo?.email ||
                devUserId ||
                localStorage.getItem('loginemail') ||
                localStorage.getItem('userId');
            const currentUserRole = userRole || tokenUserInfo?.role || localStorage.getItem('userRole');

            if (currentUserId) {
                try {
                    const response = await getUserAccessById(currentUserId);

                    if (response) {
                        // Set the user data with available information
                        const processedUserData: UserData = {
                            email: currentUserId,
                            role: currentUserRole || undefined,
                            userType: tokenUserInfo?.userType,
                            firstName: tokenUserInfo?.name?.split(' ')[0],
                            lastName: tokenUserInfo?.name?.split(' ').slice(1).join(' '),
                            ...response // Spread any additional data from the API
                        };

                        setUserData(processedUserData);
                    } else {
                        // Set basic data from available info including token
                        setUserData({
                            email: currentUserId,
                            role: currentUserRole || undefined,
                            userType: tokenUserInfo?.userType,
                            firstName: tokenUserInfo?.name?.split(' ')[0],
                            lastName: tokenUserInfo?.name?.split(' ').slice(1).join(' ')
                        });
                    }
                } catch (error) {
                    console.error('Error loading user data:', error);
                    setUserData({
                        email: currentUserId,
                        role: currentUserRole || undefined,
                        userType: tokenUserInfo?.userType,
                        firstName: tokenUserInfo?.name?.split(' ')[0],
                        lastName: tokenUserInfo?.name?.split(' ').slice(1).join(' ')
                    });
                }
            } else {
                // Still show what we can
                setUserData({
                    email: 'Unknown User',
                    role: currentUserRole || 'User'
                });
            }
        };

        const loadUserSettings = async () => {
            // For getUserPreferences API, use UserId from token (MongoDB ObjectId format)
            const userIdForLoad = tokenUserInfo?.userId || userId || tokenUserId || devUserId;

            if (userIdForLoad) {
                try {
                    setIsLoadingSettings(true);
                    const response = await getUserPreferences(userIdForLoad);
                    if (response) {
                        const loadedSettings = {
                            timeZone: response.timeZone || '',
                            uom: response.uom || 'Metric'
                        };
                        setSettings(loadedSettings);
                        if (response.timeZone) {
                            localStorage.setItem('userTimezone', response.timeZone);
                        }
                    }
                } catch (error) {
                     console.error('Error loading preferences:', error); 
                } finally {
                    setIsLoadingSettings(false);
                }
            } else {
                setIsLoadingSettings(false);
            }
        };

        loadUserData();
        loadTimeZones();
        loadUnitsOfMeasurements();
        loadUserSettings();
    }, [userId, tokenUserInfo?.email]); // Depend on email since that's what APIs use

    // Filter time zones based on search
    useEffect(() => {
        if (timeZoneSearch.trim() === '') {
            setFilteredTimeZones(timeZoneOptions);
        } else {
            const filtered = timeZoneOptions.filter(option =>
                option.label.toLowerCase().includes(timeZoneSearch.toLowerCase())
            );
            setFilteredTimeZones(filtered);
        }
    }, [timeZoneSearch, timeZoneOptions]);

    // Set the label when timezone options are loaded and a timezone is already selected
    useEffect(() => {
        if (settings.timeZone && timeZoneOptions.length > 0 && !selectedTimeZoneLabel) {
            const foundOption = timeZoneOptions.find(opt => opt.value === settings.timeZone);
            if (foundOption) {
                setSelectedTimeZoneLabel(foundOption.label);
            }
        }
    }, [settings.timeZone, timeZoneOptions, selectedTimeZoneLabel]);

    const handleInputChange = (field: keyof UserSettings, value: string) => {
        setSettings(prev => ({
            ...prev,
            [field]: value
        }));
    };

    const handleTimeZoneSelect = (value: string, label: string) => {
    handleInputChange('timeZone', value);
    setSelectedTimeZoneLabel(label);
    setTimeZoneSearch('');
    setIsTimeZoneDropdownOpen(false);
    };

    const handleTimeZoneSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setTimeZoneSearch(e.target.value);
    setIsTimeZoneDropdownOpen(true);
    };

    const handleDialogCancel = (e: React.SyntheticEvent<HTMLDialogElement>) => {
        e.preventDefault();
        if (isTimeZoneDropdownOpen) {
            setIsTimeZoneDropdownOpen(false);
            setTimeZoneSearch('');
        } else {
            onClose();
        }
    };

    useEffect(() => {
        const dialog = dialogRef.current;
        dialog?.showModal();
        return () => {
            dialog?.close();
        };
    }, []);

    // Handle click outside timezone dropdown
    useEffect(() => {
        const handleClickOutside = (e: MouseEvent) => {
            const target = e.target as Element;
            // Only close if not clicking inside dropdown or input
            if (!target.closest('.searchable-select-container') && !target.closest('.searchable-dropdown')) {
                setIsTimeZoneDropdownOpen(false);
                setTimeZoneSearch('');
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [isTimeZoneDropdownOpen]);

    // Handle body scroll and navbar visibility when modal is open
    useEffect(() => {
        // Store the original body overflow style
        const originalBodyOverflow = document.body.style.overflow;
        
        // Disable body scrolling when modal is open
        document.body.style.overflow = 'hidden';
        
        // Cleanup function to restore original state when modal unmounts
        return () => {
            document.body.style.overflow = originalBodyOverflow;
        };
    }, []);

    const timeZonePlaceholder = isTimeZoneDropdownOpen
        ? "Search time zones..."
        : (selectedTimeZoneLabel || settings.timeZone || "Select a time zone");

    const getTimeZoneDropdownContent = () => {
        if (isLoadingTimeZones) {
            return <div className="dropdown-option no-results">Loading time zones...</div>;
        }

        if (filteredTimeZones.length > 0) {
            return filteredTimeZones.map(option => (
                <button
                    key={option.value}
                    type="button"
                    className={`dropdown-option ${option.value === settings.timeZone ? 'selected' : ''}`}
                    onMouseDown={e => { e.preventDefault(); handleTimeZoneSelect(option.value, option.label); }}
                    onClick={() => handleTimeZoneSelect(option.value, option.label)}
                >
                    {option.label}
                </button>
            ));
        }

        const emptyMessage = timeZoneOptions.length === 0
            ? 'No time zones configured - Contact administrator'
            : 'No time zones found';

        return (
            <div className="dropdown-option no-results">
                {emptyMessage}
            </div>
        );
    };

    const timeZoneDropdownContent = getTimeZoneDropdownContent();

    return createPortal(
            <dialog
                ref={dialogRef}
                className="user-preferences-dialog"
                aria-labelledby="user-preferences-title"
                onCancel={handleDialogCancel}
            >
                <div className="user-preferences-modal">
                    <div className="user-preferences-header">
                        <div className="header-content">
                            <h2 id="user-preferences-title">User preferences</h2>
                            <div className="user-info">
                                <span className="username">{displayUsername}</span>
                                <span className="access-type">
                                    {userData?.userType && userData?.role
                                        ? `${userData.userType} • ${userData.role}`
                                        : userData?.role || userRole || localStorage.getItem('userRole') || 'User'}
                                </span>
                        </div>
                    </div>
                    <button
                        className="close-button"
                        onClick={onClose}
                        aria-label="Close preferences"
                    >
                        ×
                    </button>
                </div>

                <div className="user-preferences-content">
                    <div className="preferences-form">
                        <div className="form-group">
                            <label htmlFor="timeZone">Time zone</label>
                            <div className="searchable-select-container">
                                <div className="searchable-select-input userpreference-input">
                                    <input
                                        type="text"
                                        value={isTimeZoneDropdownOpen ? timeZoneSearch : (selectedTimeZoneLabel || settings.timeZone)}
                                        onChange={handleTimeZoneSearchChange}
                                        onFocus={() => { setIsTimeZoneDropdownOpen(true); setTimeZoneSearch(''); }}
                                        onClick={() => { setIsTimeZoneDropdownOpen(true); setTimeZoneSearch(''); }}
                                        placeholder={timeZonePlaceholder}
                                        className="preferences-select searchable-input"
                                        readOnly={!isTimeZoneDropdownOpen}
                                    />
                                    <span className={`dropdown-arrow Userpreference-arrow ${isTimeZoneDropdownOpen ? 'open' : ''}`}><svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                                        <path d="M4.16797 7.5L10.0013 13.3333L15.8346 7.5" stroke="#FCFCFD" strokeWidth="1.66667" strokeLinecap="round" strokeLinejoin="round" />
                                    </svg>
                                    </span>
                                </div>
                                {isTimeZoneDropdownOpen && (
                                    <div className="searchable-dropdowns user-preference-dropdown">
                                        {timeZoneDropdownContent}
                                    </div>
                                )}
                            </div>
                        </div>
                        {/* ...existing code... */}
                    </div>
                </div>

                <div className="user-preferences-footer">
                    {saveError && (
                        <div className="error-message">
                            {saveError}
                        </div>
                    )}
                    {saveSuccess && (
                        <div className="success-message">
                            Preferences saved successfully!
                        </div>
                    )}
                    <button
                        className="save-preferences-btn"
                        onClick={handleSave}
                        disabled={isSaving || isLoadingSettings || isLoadingTimeZones || isLoadingUnits}
                    >
                        {isSaving ? 'Saving...' : 'Save'}
                    </button>
                </div>

                {(isSaving || isLoadingSettings || isLoadingTimeZones || isLoadingUnits) && (
                    <div className="loader-overlay">
                        <div className="loader-wrapper">
                            <Loader />
                        </div>
                    </div>
                )}
            </div>
            </dialog>,
        document.body
    );
};

export default UserPreferences;
